import { alpha, css, keyframes, styled } from '@mui/material';

// The wipe rests on the original, slides over to show the translation, then slides back.
const SPLIT_AT_REST = '50%';
const SPLIT_REVEALED = '8%';

const clipWipe = keyframes`
  0%, 15% { clip-path: inset(0 0 0 ${SPLIT_AT_REST}); }
  45%, 65% { clip-path: inset(0 0 0 ${SPLIT_REVEALED}); }
  95%, 100% { clip-path: inset(0 0 0 ${SPLIT_AT_REST}); }
`;

const dividerWipe = keyframes`
  0%, 15% { left: ${SPLIT_AT_REST}; }
  45%, 65% { left: ${SPLIT_REVEALED}; }
  95%, 100% { left: ${SPLIT_AT_REST}; }
`;

const WIPE_ANIMATION = '5.4s cubic-bezier(0.4, 0, 0.2, 1) infinite';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

export const SFrame = styled('div')(
  ({ theme }) => css`
    position: relative;
    height: 186px;
    margin-bottom: calc(var(--paywall-gap, 20px) + 4px); // the paywall card's gap, tighter on a short screen
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 12px;
    background-color: ${theme.palette.common.white};
    overflow: hidden;
    cursor: default;
    -webkit-user-select: none;
    user-select: none;
  `,
);

export const SLayer = styled('div')(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 22px 26px;
    background-color: ${theme.palette.common.white};
  `,
);

export const STranslatedLayer = styled(SLayer)(
  () => css`
    clip-path: inset(0 0 0 ${SPLIT_AT_REST});
    animation: ${clipWipe} ${WIPE_ANIMATION};

    ${REDUCED_MOTION} {
      clip-path: inset(0 0 0 ${SPLIT_REVEALED});
      animation: none;
    }
  `,
);

export const SLanguageBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== '$side',
})<{ $side: 'left' | 'right' }>(
  ({ $side, theme }) => css`
    position: absolute;
    top: 12px;
    ${$side}: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 9px;
    border-radius: 999px;
    color: ${theme.palette.common.white};
    background-color: ${theme.palette.primary[600]};
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
  `,
);

export const SHeading = styled('span')(
  ({ theme }) => css`
    margin-top: 18px;
    color: ${theme.palette.grey[900]};
    font-weight: 700;
    font-size: 22px;
    line-height: 26px;
    letter-spacing: -0.01em;
  `,
);

export const SSteps = styled('span')(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 7px;
  `,
);

export const SStep = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${theme.palette.grey[700]};
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;

    &::before {
      content: '';
      flex-shrink: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: ${theme.palette.primary[600]};
    }
  `,
);

// The speaker's camera bubble in the corner of the frame: it stays put while the text changes.
export const SAvatar = styled('span')(
  ({ theme }) => css`
    position: absolute;
    right: 14px;
    bottom: 14px;
    width: 48px;
    height: 48px;
    border: 2px solid ${theme.palette.common.white};
    border-radius: 10px;
    background-color: ${theme.palette.grey[100]};
    box-shadow: 0 4px 12px -4px ${alpha(theme.palette.grey[900], 0.3)};
    overflow: hidden;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      border-radius: 50%;
      background-color: ${theme.palette.grey[400]};
    }

    &::before {
      top: 10px;
      width: 16px;
      height: 16px;
      margin-left: -8px;
    }

    &::after {
      bottom: -10px;
      width: 34px;
      height: 22px;
      margin-left: -17px;
    }
  `,
);

export const SDivider = styled('span')(
  () => css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${SPLIT_AT_REST};
    width: 0;
    animation: ${dividerWipe} ${WIPE_ANIMATION};

    ${REDUCED_MOTION} {
      left: ${SPLIT_REVEALED};
      animation: none;
    }
  `,
);

export const SDividerLine = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: -1px;
    width: 2px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 0 0 1px ${alpha(theme.palette.grey[900], 0.12)};
  `,
);

export const SDividerHandle = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    left: -12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-top: -12px;
    border-radius: 50%;
    color: ${theme.palette.grey[600]};
    background-color: ${theme.palette.common.white};
    box-shadow: 0 2px 8px -2px ${alpha(theme.palette.grey[900], 0.4)};
  `,
);
